import { t } from './i18n'

// Minimal SSE parser over `fetch` — EventSource can't POST and the /review
// endpoint requires a JSON body + tenant header (SPEC-022).
//
// We parse the wire format described in the WHATWG spec: events separated by
// blank lines; fields are `event:`, `data:`, and `:comment` (used for
// `:keepalive` per SPEC-022). Multi-line `data:` fields are joined with '\n'.
// Unknown fields are ignored.

export interface SseEvent {
  event: string
  data: string
}

export interface SseStreamOptions {
  url: string
  method?: 'GET' | 'POST'
  headers?: Record<string, string>
  body?: unknown
  signal?: AbortSignal
  onEvent: (evt: SseEvent) => void
}

/**
 * Opens an SSE stream. POST is the review path (JSON body). GET is the catalog
 * hub — EventSource cannot send X-Tenant-Token.
 */
export async function streamSse(opts: SseStreamOptions): Promise<void> {
  const method = opts.method ?? 'POST'
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    ...opts.headers,
  }
  if (method === 'POST') {
    headers['Content-Type'] = 'application/json'
  }
  const response = await fetch(opts.url, {
    method,
    headers,
    body: method === 'POST' ? JSON.stringify(opts.body ?? {}) : undefined,
    signal: opts.signal,
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    const err = new Error(`HTTP ${response.status}: ${text || response.statusText}`)
    ;(err as Error & { status?: number }).status = response.status
    throw err
  }
  if (!response.body) {
    throw new Error(t('SSE відповідь без тіла'))
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // Events are separated by a blank line. Handle both \n\n and \r\n\r\n.
      let idx = findEventBoundary(buffer)
      while (idx !== -1) {
        const raw = buffer.slice(0, idx)
        buffer = buffer.slice(idx + boundaryLength(buffer, idx))
        const parsed = parseSseEvent(raw)
        if (parsed) opts.onEvent(parsed)
        idx = findEventBoundary(buffer)
      }
    }
    // Flush any trailing event without a blank line (defensive; servers should
    // always terminate with one).
    const trailing = parseSseEvent(buffer)
    if (trailing) opts.onEvent(trailing)
  } finally {
    reader.releaseLock()
  }
}

function findEventBoundary(buf: string): number {
  const a = buf.indexOf('\n\n')
  const b = buf.indexOf('\r\n\r\n')
  if (a === -1) return b
  if (b === -1) return a
  return Math.min(a, b)
}

function boundaryLength(buf: string, idx: number): number {
  return buf.startsWith('\r\n\r\n', idx) ? 4 : 2
}

/** Exported for unit tests (SPEC-030). */
export function parseSseEvent(raw: string): SseEvent | null {
  if (!raw) return null
  let event = 'message'
  const dataLines: string[] = []
  let hasField = false

  for (const line of raw.split(/\r?\n/)) {
    if (!line) continue
    if (line.startsWith(':')) continue // keepalive / comment
    const sep = line.indexOf(':')
    const field = sep === -1 ? line : line.slice(0, sep)
    // Per spec, one optional space after ':' is trimmed.
    let value = sep === -1 ? '' : line.slice(sep + 1)
    if (value.startsWith(' ')) value = value.slice(1)

    if (field === 'event') {
      event = value
      hasField = true
    } else if (field === 'data') {
      dataLines.push(value)
      hasField = true
    }
    // ignore `id:` and `retry:` — we don't reconnect
  }

  if (!hasField) return null
  return { event, data: dataLines.join('\n') }
}
